import { Eye, CircleDollarSign, RotateCcw } from "lucide-react"
import type { TableAction } from "@shared/types/data-table"
import type { Payment } from "@shared/types/payment.types"
import type { UserRole } from "@shared/services/session.service"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { CopyButton } from "@shared/components/ui/CopyButton"

interface PaymentSpecificHandlers {
  onRefund?: (payment: Payment) => void
  onSendReceipt?: (payment: Payment) => void
  onCapture?: (payment: Payment) => void
  onCancel?: (payment: Payment) => void
  onRetry?: (payment: Payment) => void
  onViewCustomer?: (payment: Payment) => void
  onPreview?: (payment: Payment) => void
  onApproveRefund?: (payment: Payment) => void
  onCopyChannelId?: (payment: Payment) => void
}

export type PaymentActionHandlers = BaseActionHandlers<Payment> & PaymentSpecificHandlers

const createPaymentBulkActions = () => [
  {
    key: "flag",
    label: "Flag",
    onClick: (_ids: string[]) => {},
  },
  {
    key: "cancel",
    label: "Cancel",
    variant: "destructive" as const,
    onClick: (_ids: string[]) => {},
  },
]

// Row actions for tables - quick access operations
const createPaymentRowActions = (handlers: PaymentActionHandlers, role?: UserRole) => {
  const actions = [
    // Only expose the slide-over Preview action when a handler is wired up.
    // The payments list navigates straight to the detail page and omits it.
    ...(handlers.onPreview
      ? [
          {
            key: "preview",
            label: "Preview",
            icon: <Eye className="h-4 w-4" />,
            onClick: (payment: Payment) => handlers.onPreview?.(payment),
          },
        ]
      : []),
    {
      key: "refund",
      label: "Refund payment",
      onClick: (payment: Payment) => handlers.onRefund?.(payment),
      condition: (payment: Payment) => payment.status === "succeeded",
    },
    {
      key: "capture",
      label: "Capture payment",
      onClick: (payment: Payment) => handlers.onCapture?.(payment),
      condition: (payment: Payment) => payment.status === "uncaptured",
    },
    {
      key: "copy-channel-id",
      label: "Copy Channel ID",
      onClick: (payment: Payment) => handlers.onCopyChannelId?.(payment),
      condition: (payment: Payment) => Boolean(payment.channelId),
    },
    {
      key: "view-customer",
      label: "View customer",
      onClick: (payment: Payment) => handlers.onViewCustomer?.(payment),
      condition: (payment: Payment) => Boolean(payment.customer?.email || payment.customer?.name),
    },
  ]

  if (role === 'bank') {
    actions.push({
      key: "approve-refund",
      label: "Approve refund",
      onClick: (payment: Payment) => handlers.onApproveRefund?.(payment),
      condition: (payment: Payment) => payment.status === "refunded" || payment.status === "succeeded",
    })
  }

  return actions
}

// Panel actions for detail pages - comprehensive operations
const createPaymentPanelActions = (handlers: PaymentActionHandlers, role?: UserRole) => {
  const actions = [
    ...(role !== 'bank' ? [{
      key: "send-receipt",
      label: "Send receipt",
      onClick: (payment: Payment) => handlers.onSendReceipt?.(payment),
      condition: (payment: Payment) => payment.status === "succeeded",
    }] : []),
    {
      key: "refund",
      label: "Refund payment",
      onClick: (payment: Payment) => handlers.onRefund?.(payment),
      condition: (payment: Payment) => payment.status === "succeeded",
    },
    {
      key: "capture",
      label: "Capture payment",
      onClick: (payment: Payment) => handlers.onCapture?.(payment),
      condition: (payment: Payment) => payment.status === "uncaptured",
    },
    {
      key: "cancel",
      label: "Cancel payment",
      onClick: (payment: Payment) => handlers.onCancel?.(payment),
      condition: (payment: Payment) => payment.status === "uncaptured" || payment.status === "pending",
      variant: "destructive" as const,
    },
    {
      key: "view-customer",
      label: "View customer",
      onClick: (payment: Payment) => handlers.onViewCustomer?.(payment),
      condition: (payment: Payment) => Boolean(payment.customer?.email || payment.customer?.name),
    },
  ]

  if (role === 'bank') {
    actions.push({
      key: "approve-refund",
      label: "Approve refund",
      onClick: (payment: Payment) => handlers.onApproveRefund?.(payment),
      condition: (payment: Payment) => payment.status === "refunded" || payment.status === "succeeded",
    })
  }

  return actions
}

export const getPaymentActions = (handlers: PaymentActionHandlers = {}, role?: UserRole) => {
  const baseActions = createBaseActions('Payment', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: true,
    showAddNote: true,
    showCopyId: true,
    showExport: true,
  })

  const paymentSpecificActions = {
    bulkActions: createPaymentBulkActions(),
    rowActions: createPaymentRowActions(handlers, role),
  }

  return mergeActions(baseActions, paymentSpecificActions)
}

export const getPaymentBulkActions = (handlers: PaymentActionHandlers = {}, role?: UserRole) =>
  getPaymentActions(handlers, role).bulkActions

export const getPaymentRowActions = (handlers: PaymentActionHandlers = {}, role?: UserRole) =>
  getPaymentActions(handlers, role).rowActions

export const getPaymentPanelActions = (handlers: PaymentActionHandlers = {}, role?: UserRole) =>
  createPaymentPanelActions(handlers, role)

export const getPaymentHoverActions = (handlers: PaymentActionHandlers = {}): TableAction<Payment>[] => [
  {
    key: "copy-id",
    label: "Copy Payment ID",
    onClick: () => {},
    renderAction: (payment) => (
      <CopyButton value={payment.id} className="h-7 w-7" buttonSize="sm" />
    ),
  },
  {
    key: "capture",
    label: "Capture",
    icon: <CircleDollarSign className="h-4 w-4" />,
    onClick: (payment) => handlers.onCapture?.(payment),
    condition: (payment) => payment.status === "uncaptured",
  },
  {
    key: "retry",
    label: "Retry",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (payment) => handlers.onRetry?.(payment),
    condition: (payment) => payment.status === "failed",
  },
]
